// src/pages/ecommerce/EcommercePage.js
import React, { useState } from 'react';
import { ShoppingCart, X } from 'lucide-react';

// Import components
import ProductCard from '../../Components/ecommerce/ProductCard';
import Cart from '../../Components/ecommerce/Cart';
import CheckoutForm from '../../Components/ecommerce/CheckoutForm';
import FreeConsultationCTA from '../../Components/FreeConsultationCTA';


// Product data
const products = [
  {
    id: 1,
    name: "All Glow Body Oil",
    sizes: [
      { size: "100ml", price: 13000 },
      { size: "250ml", price: 20000 }
    ],
    description: `
    Achieve radiant, glowing skin. it nourishes, evens tone, and fades hyperpigmentation for all skin types. 
    Perfect for morning and night use.
    `,
    ingredients: ["Carrot Seed Oil", "Frankincense Essential Oil"],
    image: 'https://res.cloudinary.com/dmublarjr/image/upload/v1748438239/all-glow-body-oil_hqbdet.jpg',
    category: "Oils",
    rating: 4.6,
    reviews: [
      { id: 1, user: "Anonymous", rating: 5, comment: "Amazing product!" },
      { id: 2, user: "Guest", rating: 4, comment: "Very relaxing scent" }
    ]
  },
  {
    id: 2,
    name: "Milky Soapy Scrub",
    sizes: [
      { size: "250g", price: 12000 },
      { size: "1 litre", price: 30000 }
    ],
    description: `
    Milky Soapy Scrub exfoliates, smooths, and softens skin, 
    enhancing product absorption. Use twice weekly."
    `,
    image: 'https://res.cloudinary.com/dmublarjr/image/upload/v1748438240/milky-soapy-scrub_szrwv5.jpg',
    category: "Body Scrubs",
    rating: 4.7,
    reviews: []
  },
  {
    id: 3,
    name: "Mystic Body Oil",
    sizes: [
      { size: "100ml", price: 15000 },
      { size: "250ml", price: 25000 }
    ],
    description: `
    Lightening oil for all skin types: revives dull skin, 
    fades spots, and enhances glow. Best for night use.
    `,
    ingredients: ["Turmeric", "Kojic Acid"],
    image: 'https://res.cloudinary.com/dmublarjr/image/upload/v1748438240/mystic-body-oil_faoqkx.jpg',
    category: "Oils",
    rating: 4.8,
    reviews: []
  },
  {
    id: 4,
    name: "Recovery Facial Oil",
    sizes: [
      { size: "50ml", price: 10000 }
    ],
    description: `
    Treats redness, uneven tone, acne, and spots. Use morning and 
    night after moisturizer or serum.  KEY INGREDIENTS: Rosehip & Bakuchiol Oil."
    `,
    ingredients: ["Rosehip Oil", "Bakuchiol Oil"],
    image: 'https://res.cloudinary.com/dmublarjr/image/upload/v1748438240/recovery-facial-oil_i1wcby.jpg',
    category: "Oils",
    rating: 4.9,
    reviews: []
  },
  {
    id: 5,
    name: "Recovery Night Cream",
    sizes: [
      { size: "30g", price: 8000 }
    ],
    description: `
    Lightening Face Cream treats spots, dark areas, and hyperpigmentation. 
    Best for night use.
    `,
    ingredients: ["Kojic Acid", "Arbutin"],
    image: 'https://res.cloudinary.com/dmublarjr/image/upload/v1748438240/recovery-night-cream_zfghbe.jpg',
    category: "Body Creams",
    rating: 4.5,
    reviews: []
  },
  {
    id: 6,
    name: "Goat Milk Bar Soap",
    sizes: [
      { size: "100g", price: 6000 }
    ],
    description: `
    "Goat Milk Bar Soap nourishes and deeply cleanses sensitive skin. 
    Suitable for face and body."
    `,
    image: 'https://res.cloudinary.com/dmublarjr/image/upload/v1748438240/goat-milk-bar-soap_c3j5fo.jpg',
    category: "Aromatherapy",
    rating: 4.8,
    reviews: []
  }
];


const EcommercePage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState('browsing'); // browsing, checkout, confirmation
  const [orderDetails, setOrderDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const addToCart = (product, quantity) => {
    setCartItems(items => {
      const existingItem = items.find(item => 
        item.id === product.id && item.selectedSize.size === product.selectedSize.size
      );
      if (existingItem) {
        return items.map(item =>
          (item.id === product.id && item.selectedSize.size === product.selectedSize.size)
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...items, { ...product, quantity }];
    });
  };

  const updateQuantity = (productId, size, newQuantity) => {
    if (newQuantity < 1) return;
    setCartItems(items =>
      items.map(item =>
        (item.id === productId && item.selectedSize.size === size)
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const removeItem = (productId) => {
    setCartItems(items => items.filter(item => item.id !== productId));
  };

  const totalAmount = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );


  return (
    <div className="max-w-6xl bg-primary-lighter mx-auto px-4 py-8 pt-28 mb-5 md:mb-10">
      {/* Header without cart icon */}
      <div className="flex justify-between items-center mb-8 border-b-2 border-dim-dark pb-4">
        <h1 className="text-2xl text-dim font-bold">Skincare Products</h1>
      </div>

      {/* Fixed Cart Icon */}
      <button
        onClick={() => setIsCartOpen(true)}
        className="fixed bottom-4 sm:bottom-6 left-4 sm:left-6 z-50 p-3 sm:p-4 rounded-full bg-dim-dark shadow-lg hover:bg-accent-hard transition-colors duration-200 flex items-center justify-center"
        aria-label="Open Cart"
      >
        <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-white hover:md:text-dim-dark" />
        {cartItems.length > 0 && (
          <span className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-accent-hard text-white text-xs font-semibold rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-[10px] sm:text-xs">
            {cartItems.length}
          </span>
        )}
      </button>

      {/* Main content */}
      {checkoutStep === 'browsing' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 gap-2 sm:gap-3 p-2 sm:p-4">
          {products.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              addToCart={addToCart}
            />
          ))}
        </div>
      )}

      {/* Cart sidebar */}
      {isCartOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60]">
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-lg">
            <div className="p-4">
              <button
                onClick={() => setIsCartOpen(false)}
                className="absolute top-4 right-4 p-2 rounded-md hover:bg-gray-100"
              >
                <X className="w-6 h-6" />
              </button>
              <Cart
                items={cartItems}
                updateQuantity={updateQuantity}
                removeItem={removeItem}
                totalAmount={totalAmount}
              />
              {cartItems.length > 0 && (
                <button
                  onClick={() => {
                    setCheckoutStep('checkout');
                    setIsCartOpen(false);
                  }}
                  className="w-full mt-4 bg-accent-light text-white py-2 rounded-md hover:bg-accent-hard"
                >
                  Proceed to Checkout
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Checkout form */}
      {checkoutStep === 'checkout' && (
        <div className="max-w-md mx-auto bg-secondary font-body p-6 bg-secondary rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold mb-4">Checkout Form</h2>
          <CheckoutForm
            isLoading={isLoading}
            totalAmount={totalAmount}
            items={cartItems}
          />
        </div>
      )}

      {/* Order confirmation */}
      {checkoutStep === 'confirmation' && orderDetails && (
        <div className="max-w-md mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4">Order Confirmed!</h2>
          <p className="mb-2">Order ID: {orderDetails.orderId}</p>
          <p className="mb-4">Thank you for your purchase!</p>
          <button
            onClick={() => setCheckoutStep('browsing')}
            className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
          >
            Continue Shopping
          </button>
        </div>
      )}

      {/* Free Consultation CTA */}
      <FreeConsultationCTA 
        message="I'm interested in a free skincare consultation. Can you help me?"
      />
    </div>
  );
};

export default EcommercePage;